import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import { reportService } from '@/data/reportService';
import { SalesSummary } from '@/types/SalesSummary.interface';
import {
  businessDayToday,
  getBranchTimezone,
  localHour
} from '@/utils/businessDay';
import {
  DEFAULT_ENTRY_MODE,
  ENTRY_MODE_STORAGE_KEY,
  EntryMode,
  isEntryMode,
  presetRange,
  ReportPreset,
  salesSummaryParams,
  windowParams
} from '@/utils/salesReport';
import { exportService } from '@/data/exportService';
import ExportButton from '@/components/ExportButton';
import { compareSummaries, previousWindow } from '@/utils/reportCompare';
import KPICards from '@/components/dashboard/KPICards';
import WasteStats from '@/components/dashboard/WasteStats';
import ReportPresetBar, { ReportRangeValue } from './ReportPresetBar';
import EntryModeToggle from './EntryModeToggle';
import {
  FreeOrdersCard,
  HourlyCard,
  LoyaltyCard,
  PaymentTypesCard,
  PeakHoursCard,
  ReceivablesCard,
  SalesDetailsCard,
  TopProductsCard,
  TotalsCards,
  TrendCard,
  VouchersCard
} from './SalesReportSections';

interface SalesReportViewProps {
  branchId: number | null;
  companyId: number | null;
  /** Şube yöneticisinin şubesi henüz bilinmiyorsa istek atılmaz. */
  ready: boolean;
}

const DEFAULT_PRESET: Exclude<ReportPreset, 'custom'> = 'today';

const rangeFor = (
  preset: Exclude<ReportPreset, 'custom'>
): ReportRangeValue => ({
  preset,
  ...presetRange(preset, businessDayToday(getBranchTimezone()))
});

/**
 * "Satış Raporu" — web POS "Raporlar" ekranıyla AYNI rapor: aynı uç
 * (`GET /reports/sales-summary`), aynı parametreler, aynı bölüm sırası:
 * Toplam Satış · Ortalama Sepet · (sipariş durumları) · Ödeme Türleri ·
 * Satış Detayları · Ücretsiz Verilen · Cari Tahsilat · Saatlik + Yoğun Saatler
 * · Trend · En Çok Satanlar · Zayi · Kuponlar · Sadakat.
 */
const SalesReportView = ({
  branchId,
  companyId,
  ready
}: SalesReportViewProps) => {
  const { t } = useTranslation();
  const [range, setRange] = useState<ReportRangeValue>(() =>
    rangeFor(DEFAULT_PRESET)
  );
  const [wastePage, setWastePage] = useState(1);
  const [summary, setSummary] = useState<SalesSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Girişlerin ortalamaya katılması bu tarayıcıda hatırlanır (yalnız görünüm;
  // rakamlar backend'de iki mod için de hazır gelir).
  const [entryMode, setEntryMode] = useState<EntryMode>(() => {
    try {
      const saved = localStorage.getItem(ENTRY_MODE_STORAGE_KEY);
      return isEntryMode(saved) ? saved : DEFAULT_ENTRY_MODE;
    } catch {
      return DEFAULT_ENTRY_MODE;
    }
  });

  const handleEntryModeChange = (mode: EntryMode) => {
    setEntryMode(mode);
    try {
      localStorage.setItem(ENTRY_MODE_STORAGE_KEY, mode);
    } catch {
      // Depolama kapalıysa seçim yalnız bu oturumda geçerli.
    }
  };
  const generation = useRef(0);
  // Önceki dönem (kıyas rozetleri): zayi sayfasından bağımsız, yalnız aralık
  // veya kapsam değişince çekilir. Hata olursa rozetler gizlenir.
  const [previous, setPrevious] = useState<SalesSummary | null>(null);
  const previousGeneration = useRef(0);
  const prevRange = useMemo(() => previousWindow(range), [range]);

  useEffect(() => {
    if (!ready) return;
    const current = ++previousGeneration.current;
    setPrevious(null);
    reportService
      .getSalesSummary({
        branchId,
        companyId,
        startDate: prevRange.startDate,
        endDate: prevRange.endDate,
        timezone: getBranchTimezone(),
        wastePage: 1
      })
      .then((result) => {
        if (current === previousGeneration.current) setPrevious(result);
      })
      .catch((err) => {
        console.warn('Previous period for comparison failed:', err);
      });
  }, [ready, branchId, companyId, prevRange]);

  const fetchSummary = useCallback(async () => {
    if (!ready) return;
    const current = ++generation.current;
    setLoading(true);
    setError(false);
    try {
      const result = await reportService.getSalesSummary({
        branchId,
        companyId,
        startDate: range.startDate,
        endDate: range.endDate,
        timezone: getBranchTimezone(),
        wastePage
      });
      if (current === generation.current) setSummary(result);
    } catch (err) {
      console.error('Error fetching sales summary:', err);
      if (current === generation.current) setError(true);
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [ready, branchId, companyId, range, wastePage]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Kapsam değişince zayi listesi başa döner.
  useEffect(() => {
    setWastePage(1);
  }, [branchId, companyId]);

  const handlePresetSelect = (preset: Exclude<ReportPreset, 'custom'>) => {
    setWastePage(1);
    setRange(rangeFor(preset));
  };

  const handleCustomApply = (startDate: string, endDate: string) => {
    setWastePage(1);
    setRange({ preset: 'custom', startDate, endDate });
  };

  const timezone = summary?.window.timezone ?? getBranchTimezone();
  // Bugünü de içeren çok günlü aralık (Son 7 gün vb.) yarım kalan bugünü
  // önceki dönemin tam günüyle kıyaslardı: gün içinde sahte düşüş. Saatlik
  // ayrıntı yalnız tek gün için doğru kesilebildiğinden bunlarda rozet yok.
  const rangeIsOpen =
    range.preset !== 'today' && range.endDate === businessDayToday(timezone);
  // Yalnız iki yanıt da seçili aralığa ve aynı kapsama aitse kıyasla (biri
  // hâlâ yüklenirken eski özetle yeni önceki dönem, ya da şube değişince
  // iki farklı şube eşleşmesin).
  const comparison =
    summary &&
    previous &&
    !rangeIsOpen &&
    summary.window.startDate === range.startDate &&
    summary.window.endDate === range.endDate &&
    previous.window.startDate === prevRange.startDate &&
    previous.window.endDate === prevRange.endDate &&
    JSON.stringify(summary.scope) === JSON.stringify(previous.scope)
      ? compareSummaries(summary, previous, {
          entryMode,
          preset: range.preset,
          previousWindow: prevRange,
          nowHour: localHour(timezone)
        })
      : null;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <ReportPresetBar
          value={range}
          onPresetSelect={handlePresetSelect}
          onCustomApply={handleCustomApply}
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          {summary && (
            <Typography variant="h5" fontWeight={600}>
              {t('salesReport.window', windowParams(summary))}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{ textTransform: 'none' }}
            color="text.secondary"
          >
            {t('salesReport.window.hint')}
          </Typography>
        </Box>
        {loading && summary && <CircularProgress size={20} />}
        <ExportButton
          disabled={!summary}
          onExport={() =>
            // Same request as the screen (shared salesSummaryParams).
            exportService.exportSalesReport(
              salesSummaryParams({
                branchId,
                companyId,
                startDate: range.startDate,
                endDate: range.endDate,
                timezone: getBranchTimezone()
              })
            )
          }
        />
        <Tooltip title={t('salesReport.refresh')}>
          <span>
            <IconButton
              color="primary"
              onClick={fetchSummary}
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 3 }}>
        <EntryModeToggle value={entryMode} onChange={handleEntryModeChange} />
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={fetchSummary}>
              {t('salesReport.retry')}
            </Button>
          }
        >
          {t('salesReport.error')}
        </Alert>
      )}

      {!summary && loading && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      )}

      {summary && (
        <Grid
          container
          spacing={3}
          sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}
        >
          <Grid item xs={12}>
            <TotalsCards
              summary={summary}
              entryMode={entryMode}
              comparison={comparison}
              preset={range.preset}
            />
          </Grid>
          <Grid item xs={12}>
            <KPICards
              isLoading={false}
              stats={{
                pendingOrders: summary.orders.statusCounts.preparing,
                readyOrders: summary.orders.statusCounts.ready,
                canceledOrders: summary.orders.statusCounts.canceled
              }}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <PaymentTypesCard summary={summary} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <SalesDetailsCard summary={summary} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <FreeOrdersCard summary={summary} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <ReceivablesCard summary={summary} />
          </Grid>
          <Grid item xs={12} lg={8}>
            <HourlyCard summary={summary} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <PeakHoursCard summary={summary} />
          </Grid>
          <Grid item xs={12}>
            <TrendCard summary={summary} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TopProductsCard summary={summary} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <WasteStats
              waste={summary.waste}
              timezone={timezone}
              onPageChange={setWastePage}
              pageLoading={loading}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <VouchersCard summary={summary} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <LoyaltyCard summary={summary} />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default SalesReportView;
